import { useMemo, useState } from "react";
import { parseSource } from "@/api/client";
import { fragmentUrl } from "@/lib/citations";
import { attachmentPreviewUrl, viewerUrl } from "@/lib/preview";
import DocViewer from "./DocViewer";
import type { SourceMeta } from "@/api/types";
import { ExternalLink, Eye, RotateCcw, Clock, AlertTriangle, Quote } from "lucide-react";

interface Props {
  sources: string[];
  sourceMeta?: SourceMeta[];              // v3.3: 번호·신선도·근거 스니펫
  activeIndex?: number | null;            // v3.3: 각주 클릭으로 강조된 항목(1-base)
  registerItemRef?: (index: number, el: HTMLDivElement | null) => void; // 스크롤용
  // 아래 3개가 모두 있을 때만 '제외(deselect) 후 재생성' UI가 활성화된다.
  question?: string;
  customer?: string;
  onRegenerate?: (question: string, excludeUrls: string[], customer?: string) => void;
}

interface Item {
  index: number;
  label: string;
  title: string;
  url: string | null;
  quote?: string;
  updated_at?: string;
  stale?: boolean;
}

export default function SourceList({
  sources,
  sourceMeta,
  activeIndex,
  registerItemRef,
  question,
  customer,
  onRegenerate,
}: Props) {
  const [preview, setPreview] = useState<{ title: string; url: string } | null>(null);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  // 근거 스니펫을 펼친 항목(각주 클릭 외에 수동 토글도 지원)
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  // sourceMeta 우선, 없으면 sources 문자열 파싱으로 폴백(캐시 응답 등)
  const items = useMemo<Item[]>(() => {
    if (sourceMeta?.length) {
      return sourceMeta.map((m) => ({
        index: m.index,
        label: m.label,
        title: m.title,
        url: m.url,
        quote: m.quote,
        updated_at: m.updated_at,
        stale: m.stale,
      }));
    }
    return sources.map((s, i) => {
      const p = parseSource(s);
      return { index: i + 1, label: p.label, title: p.title, url: p.url };
    });
  }, [sourceMeta, sources]);

  if (!items.length) return null;

  const selectable = items.some((p) => !!p.url);
  const interactive = !!onRegenerate && !!question && selectable;
  const keptCount = items.filter((p) => !p.url || !excluded.has(p.url)).length;

  const toggle = (url: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(url)) next.delete(url);
      else next.add(url);
      return next;
    });
  };

  const toggleEvidence = (n: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  const runRegenerate = () => {
    if (!interactive || excluded.size === 0 || keptCount < 1) return;
    onRegenerate!(question!, Array.from(excluded), customer);
  };

  return (
    <div className="mt-3 space-y-1.5 border-t border-border pt-2">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-muted-foreground">참고 문서</div>
        {interactive && (
          <span className="text-[11px] text-muted-foreground">관련 없는 자료는 체크 해제하세요</span>
        )}
      </div>

      {items.map((p) => {
        const isExcluded = !!p.url && excluded.has(p.url);
        const isActive = activeIndex === p.index;
        const showEvidence = (isActive || expanded.has(p.index)) && !!p.quote;
        return (
          <div
            key={p.index}
            ref={(el) => registerItemRef?.(p.index, el)}
            className={
              "rounded-md px-2 py-1.5 text-xs transition-colors " +
              (isActive
                ? "bg-[#0E1E2D]/10 ring-2 ring-[#0E1E2D]/40"
                : isExcluded
                  ? "bg-muted/30 opacity-50"
                  : "bg-muted/60")
            }
          >
            <div className="flex items-start gap-1.5">
              {interactive && p.url ? (
                <input
                  type="checkbox"
                  checked={!isExcluded}
                  onChange={() => toggle(p.url!)}
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#0E1E2D]"
                  aria-label={isExcluded ? "이 문서 다시 포함" : "이 문서 제외"}
                  title={isExcluded ? "이 문서 다시 포함" : "이 문서 제외"}
                />
              ) : (
                <span
                  className="mt-0.5 inline-flex h-4 min-w-[1rem] shrink-0 items-center justify-center rounded bg-[#0E1E2D] px-1 text-[10px] font-semibold leading-none text-white"
                  title={`근거 ${p.index}번`}
                >
                  {p.index}
                </span>
              )}
              <span className="min-w-0 flex-1">
                {p.label && <span className="mr-1 text-muted-foreground">[{p.label}]</span>}
                <span className={"break-words " + (isExcluded ? "line-through" : "")}>{p.title}</span>
                {/* 신선도 배지 */}
                {p.updated_at && (
                  <span
                    className={
                      "ml-1.5 inline-flex items-center gap-0.5 rounded px-1 py-px text-[10px] align-middle " +
                      (p.stale ? "bg-amber-100 text-amber-700" : "bg-muted text-muted-foreground")
                    }
                    title={p.stale ? "오래된 문서일 수 있습니다" : "마지막 갱신일"}
                  >
                    {p.stale ? <AlertTriangle className="h-2.5 w-2.5" /> : <Clock className="h-2.5 w-2.5" />}
                    갱신 {p.updated_at}
                  </span>
                )}
              </span>
              {p.quote && (
                <button
                  onClick={() => toggleEvidence(p.index)}
                  className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                  aria-label="근거 문장 보기"
                  title="근거 문장 보기"
                >
                  <Quote className="h-3.5 w-3.5" />
                </button>
              )}
              {p.url && (
                <>
                  <button
                    onClick={() => setPreview({ title: p.title, url: p.url! })}
                    className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label="미리보기"
                    title="미리보기"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </button>
                  <a
                    href={fragmentUrl(p.url, p.quote)}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground"
                    aria-label="원문에서 열기"
                    title="원문에서 근거 위치로 열기"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </>
              )}
            </div>

            {/* 근거 스니펫 패널 — 각주 클릭/토글 시 표시 */}
            {showEvidence && (
              <div className="mt-1.5 border-l-2 border-[#0E1E2D]/40 bg-card/60 py-1 pl-2 pr-1 text-[11px] leading-relaxed text-foreground/90">
                <span className="rounded bg-yellow-100 px-0.5 text-foreground">“{p.quote}”</span>
                {p.url && (
                  <a
                    href={fragmentUrl(p.url, p.quote)}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-1 whitespace-nowrap font-medium text-[#1C3954] hover:underline"
                  >
                    원문에서 보기 →
                  </a>
                )}
              </div>
            )}
          </div>
        );
      })}

      {interactive && excluded.size > 0 && (
        <div className="flex items-center justify-between gap-2 pt-1">
          <span className="text-[11px] text-muted-foreground">
            {excluded.size}건 제외 · {keptCount}건 사용
          </span>
          <button
            onClick={runRegenerate}
            disabled={keptCount < 1}
            className="inline-flex items-center gap-1 rounded-md bg-[#0E1E2D] hover:bg-[#1C3954] px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-40"
            title="선택한 문서만 근거로 다시 답변"
          >
            <RotateCcw className="h-3.5 w-3.5" /> 선택한 문서로 다시 답변
          </button>
        </div>
      )}

      {preview && (
        <DocViewer
          name={preview.title}
          // 첨부(/attachments/{id})는 미리보기(PDF 변환) URL 로 바꿔서 열어야 문서가 보인다.
          url={viewerUrl(preview.url)}
          downloadUrl={attachmentPreviewUrl(preview.url) ? preview.url : undefined}
          note="내용이 보이지 않으면 상단 “새 탭”을 이용하세요."
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
